import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/core/auth/AuthContext'
import { LoadingButton } from '@/ui/components/LoadingButton'
import { LoginCredentials } from '@/core/auth/types'
import '../styles/Auth.css'

export const SignIn: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuth()
  
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  // Always redirect to base path, let RoleBasedRouter handle the default landing page
  const from = '/dashboard'

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setLoading(true)
    
    try {
      await login(formData)
      navigate(from, { replace: true })
    } catch (err: any) {
      setErrors({ general: err.message || 'Login failed' })
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleAuth = () => {
    // TODO: Implement Google OAuth
    // TODO: Google authentication not implemented yet
  }

  return (
    <div className="auth-layout">
      <aside className="hero-pane">
        <img 
          src="/images/home-banner.jpg"
          alt="Hero image"
          className="hero-image"
        />
        <div className="hero-overlay"></div>
        <div className="hero-banner"></div>
        <div className="hero-brand">
          <div className="shield-logo">
            <img 
              src="/images/logo.svg" 
              alt="Logo" 
              width="auto" 
              height="128"
              style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.25))' }}
            />
          </div>
        </div>
      </aside>

      <main className="form-pane">
        <div className="form-container">
          <h1>Welcome to your ChatelainXpert space</h1>
          <p className="lead">Find all your expertises in one place</p>

          <h2>Sign In</h2>

          <button 
            type="button" 
            className="google-btn"
            onClick={handleGoogleAuth}
            aria-label="Sign in with Google"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>

          <p className="divider">Or</p>

          {errors.general && (
            <div className="error-banner">
              {errors.general}
            </div>
          )}

          <form onSubmit={handleSubmit} className="signin-form">
            <div className="input-group">
              <label htmlFor="email" className="sr-only">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="E-mail"
                aria-required="true"
                className={errors.email ? 'error' : ''}
              />
              {errors.email && (
                <p className="error">{errors.email}</p>
              )}
            </div>

            <div className="input-group">
              <label htmlFor="password" className="sr-only">Password</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Password"
                  aria-required="true"
                  className={errors.password ? 'error' : ''}
                />
                <button
                  type="button"
                  className="toggle-pw"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="error">{errors.password}</p>
              )}
            </div>

            <div className="forgot-password">
              <button type="button" className="link-btn">
                Forgot password?
              </button>
            </div>

            <LoadingButton
              type="submit"
              className="btn-secondary"
              isLoading={loading}
            >
              Sign In
            </LoadingButton>
          </form>

          <p className="auth-switch">
            Don&apos;t have an account yet?{' '}
            <Link to="/signup" className="link-btn">
              Create an account
            </Link>
          </p>
        </div>
      </main>
    </div>
  )
}